import { useEffect, useState } from 'react'
import { Badge, Button, Col, FloatingLabel, Form, Row, Spinner, Table, Alert } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { fetchRezervaceCiselniky, searchRezervace } from '../services/api/rezervace'

const DEFAULT_FILTERS = {
  q: '', od: '', do: '', stav: '', typ: '', ukon: '', skupina: '', stani: '', ma_uschovu: '',
  radit: 'termin', smer: 'desc', limit: 50
}
const STANDS = [{ id: 1, title: 'osobní 1' }, { id: 2, title: 'osobní 2' }, { id: 3, title: 'truck' }]

const exportRow = (item) => ({
  Termín: moment(item.zacatek).format('DD.MM.YYYY HH:mm'),
  Konec: moment(item.konec).format('DD.MM.YYYY HH:mm'),
  SPZ: item.spz || '',
  Jméno: item.jmeno || '',
  Firma: item.firma || '',
  Telefon: item.telefon || '',
  Email: item.email || '',
  Vozidlo: item.typ_vozidla_nazev || '',
  'Značka a model': item.znacka || '',
  'Servisní úkon': item.servisni_ukon_nazev || '',
  'Varianta přezutí': item.varianta_prezuti_nazev || '',
  Skupina: item.skupina_nazev || '',
  Stání: STANDS.find((stand) => stand.id === Number(item.stani))?.title || item.stani || '',
  Stav: item.stav_nazev || '',
  STK: item.stk ? moment(item.stk).format('DD.MM.YYYY') : '',
  Tachometr: item.tachometr ?? '',
  Úschova: Number(item.ma_uschovu) === 1 ? 'Ano' : 'Ne',
  Poznámka: item.poznamka || ''
})

const downloadBlob = (content, type, filename) => {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export default function ReservationSearch() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [lookups, setLookups] = useState(null)
  const [result, setResult] = useState({ data: [], pagination: { strana: 1, celkem: 0, stran: 1, limit: 50 } })
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const load = async (page = 1, nextFilters = filters) => {
    setLoading(true)
    setError('')
    try {
      setResult(await searchRezervace({ ...nextFilters, strana: page }))
    } catch (err) {
      setError(err.message || 'Rezervace se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRezervaceCiselniky().then(setLookups).catch((err) => setError(err.message))
    load(1, DEFAULT_FILTERS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        isFilterOpen &&
        !event.target.closest('.offcanvas-custom') &&
        !event.target.closest('#reservation-search-filter-btn')
      ) {
        setIsFilterOpen(false)
      }
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsFilterOpen(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isFilterOpen])

  const loadAllFiltered = async () => {
    const first = await searchRezervace({ ...filters, strana: 1, limit: 200 })
    const rows = [...(first.data || [])]
    for (let page = 2; page <= Number(first.pagination?.stran || 1); page += 1) {
      const next = await searchRezervace({ ...filters, strana: page, limit: 200 })
      rows.push(...(next.data || []))
    }
    return rows.map(exportRow)
  }

  const exportData = async (format) => {
    setExporting(true)
    setError('')
    try {
      const XLSX = await import('xlsx')
      const rows = await loadAllFiltered()
      const filename = `rezervace-${moment().format('YYYY-MM-DD-HHmm')}`
      if (format === 'csv') {
        const sheet = XLSX.utils.json_to_sheet(rows)
        const csv = XLSX.utils.sheet_to_csv(sheet, { FS: ';' })
        downloadBlob(`\uFEFF${csv}`, 'text/csv;charset=utf-8', `${filename}.csv`)
      } else {
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Rezervace')
        XLSX.writeFile(workbook, `${filename}.xlsx`)
      }
    } catch (err) {
      setError(err.message || 'Export se nepodařilo vytvořit.')
    } finally {
      setExporting(false)
    }
  }

  const set = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }))
  const pagination = result.pagination || { strana: 1, celkem: 0, stran: 1 }
  const activeFilterCount = ['q', 'od', 'do', 'stav', 'typ', 'ukon', 'skupina', 'stani', 'ma_uschovu']
    .filter((key) => String(filters[key] || '').trim() !== '').length

  return (
    <div className='py-3'>
      {error ? <Alert variant='danger'>{error}</Alert> : null}
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2'>
        <div className='d-flex align-items-center gap-2'>
          <Button id='reservation-search-filter-btn' variant={activeFilterCount ? 'primary' : 'outline-primary'} onClick={() => setIsFilterOpen((open) => !open)}>
            <i className='bi bi-sliders me-2'></i>Filtr
            {activeFilterCount ? <Badge bg='light' text='primary' pill className='ms-2'>{activeFilterCount}</Badge> : null}
          </Button>
          <span className='text-muted small'>Nalezeno: <strong>{pagination.celkem}</strong></span>
        </div>
        <span className='text-muted small'>Strana {pagination.strana} z {pagination.stran}</span>
      </div>

      <div className={`offcanvas-custom ${isFilterOpen ? 'open' : ''}`}>
        <Form className='d-flex flex-column h-100' onSubmit={(e) => { e.preventDefault(); load(1); setIsFilterOpen(false) }}>
          <div className='d-flex justify-content-between align-items-center p-4 mb-3'>
            <h4 className='my-1'>Filtrování rezervací</h4>
            <div>
              <button type='button' className='btn' title='Zrušit vše' onClick={() => { setFilters(DEFAULT_FILTERS); load(1, DEFAULT_FILTERS) }}><i className='bi bi-trash'></i></button>
              <button type='button' className='btn' title='Zavřít' onClick={() => setIsFilterOpen(false)}><i className='bi bi-x-lg'></i></button>
            </div>
          </div>
          <div className='offcanvas-custom-body flex-grow-1 overflow-auto'>
            <div className='px-4'>
              <Row className='g-2'>
                <Col xs={12}><FloatingLabel className='reservation-search-query' label='SPZ, zákazník, kontakt, značka/model nebo poznámka'><Form.Control type='search' value={filters.q} onChange={(e) => set('q', e.target.value)} placeholder='Hledat' /></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='od'><Form.Control type='date' value={filters.od} onChange={(e) => set('od', e.target.value)} /></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='do'><Form.Control type='date' value={filters.do} onChange={(e) => set('do', e.target.value)} /></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='stav'><Form.Select value={filters.stav} onChange={(e) => set('stav', e.target.value)}><option value=''>Vše</option>{(lookups?.stavy || []).map((x) => <option key={x.id_stavu} value={x.id_stavu}>{x.nazev}</option>)}</Form.Select></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='typ vozidla'><Form.Select value={filters.typ} onChange={(e) => set('typ', e.target.value)}><option value=''>Vše</option>{(lookups?.typyVozidel || []).map((x) => <option key={x.id_typu} value={x.id_typu}>{x.nazev}</option>)}</Form.Select></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='servisní úkon'><Form.Select value={filters.ukon} onChange={(e) => set('ukon', e.target.value)}><option value=''>Vše</option>{(lookups?.servisniUkony || []).map((x) => <option key={x.id_ukonu} value={x.id_ukonu}>{x.nazev}</option>)}</Form.Select></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='skupina'><Form.Select value={filters.skupina} onChange={(e) => set('skupina', e.target.value)}><option value=''>Vše</option>{(lookups?.skupiny || []).map((x) => <option key={x.id_skupiny} value={x.id_skupiny}>{x.nazev}</option>)}</Form.Select></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='stání'><Form.Select value={filters.stani} onChange={(e) => set('stani', e.target.value)}><option value=''>Vše</option>{STANDS.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</Form.Select></FloatingLabel></Col>
                <Col xs={12}><FloatingLabel label='úschova'><Form.Select value={filters.ma_uschovu} onChange={(e) => set('ma_uschovu', e.target.value)}><option value=''>Vše</option><option value='1'>S úschovou</option><option value='0'>Bez úschovy</option></Form.Select></FloatingLabel></Col>
              </Row>
            </div>
          </div>
          <div className='d-flex gap-2 p-4 border-top'>
            <Button type='submit' className='flex-grow-1' disabled={loading}>{loading ? <Spinner size='sm' animation='border' className='me-2' /> : <i className='bi bi-search me-2'></i>}Vyhledat</Button>
            <Button type='button' variant='outline-danger' title='Vymazat filtry' onClick={() => { setFilters(DEFAULT_FILTERS); load(1, DEFAULT_FILTERS) }}><i className='bi bi-trash'></i></Button>
          </div>
        </Form>
      </div>
      <div className='table-responsive rounded border reservation-search-page-results'>
        <Table hover className='align-middle mb-0 reservation-detail-table'>
          <thead className='table-light'><tr><th>Termín</th><th>SPZ</th><th>Zákazník</th><th>Kontakt</th><th>Vozidlo</th><th>Úkon</th><th>Poznámka</th><th>Stání</th><th>Stav</th><th></th></tr></thead>
          <tbody>
            {(result.data || []).map((item) => <tr key={item.id_rezervace}><td className='text-nowrap'>{moment(item.zacatek).format('D. M. YYYY HH:mm')}–{moment(item.konec).format('HH:mm')}</td><td className='fw-semibold'>{item.spz || '–'}{Number(item.ma_uschovu) === 1 ? <i className='bi bi-safe text-primary ms-2' title='Má úschovu'></i> : null}</td><td>{item.jmeno || '–'}{item.firma ? <div className='small text-muted'>{item.firma}</div> : null}</td><td>{item.telefon || '–'}{item.email ? <div className='small text-muted'>{item.email}</div> : null}</td><td>{item.typ_vozidla_nazev || '–'}{item.znacka ? <div className='small text-muted'>{item.znacka}</div> : null}</td><td>{item.servisni_ukon_nazev || '–'}{item.varianta_prezuti_nazev ? <div className='small text-muted'>{item.varianta_prezuti_nazev}</div> : null}</td><td className='reservation-search-note' title={item.poznamka || ''}>{item.poznamka || '–'}</td><td>{STANDS.find((x) => x.id === Number(item.stani))?.title || item.stani}</td><td><Badge bg={item.stav_barva || 'secondary'}>{item.stav_nazev || '–'}</Badge></td><td><Button size='sm' variant='outline-primary' title='Zobrazit v kalendáři' onClick={() => navigate('/reservations', { state: { reservationDate: item.zacatek, reservationId: item.id_rezervace } })}><i className='bi bi-calendar-event'></i></Button></td></tr>)}
            {!loading && !(result.data || []).length ? <tr><td colSpan='10' className='text-center text-muted py-5'>Žádné rezervace neodpovídají zadaným filtrům.</td></tr> : null}
          </tbody>
        </Table>
      </div>
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3'>
        <div className='d-flex flex-wrap gap-1'>
          <Button variant='outline-secondary' title='Zpět na kalendář' onClick={() => navigate('/reservations')}><i className='bi bi-arrow-left me-2'></i>Zpět</Button>
          <Button variant='outline-primary' disabled={exporting} onClick={() => exportData('csv')}><i className='bi bi-filetype-csv me-2'></i>CSV</Button>
          <Button variant='outline-primary' disabled={exporting} onClick={() => exportData('xlsx')}><i className='bi bi-file-earmark-excel me-2'></i>XLSX</Button>
        </div>
        <div className='d-flex gap-1'>
          <Button variant='outline-secondary' disabled={loading || pagination.strana <= 1} onClick={() => load(pagination.strana - 1)}><i className='bi bi-chevron-left me-1'></i>Předchozí</Button>
          <Button variant='outline-secondary' disabled={loading || pagination.strana >= pagination.stran} onClick={() => load(pagination.strana + 1)}>Další<i className='bi bi-chevron-right ms-1'></i></Button>
        </div>
      </div>
    </div>
  )
}
